/**
 * @file table-modal.component.tsx
 * @description Компонент модального окна фильтрации для Task Manager приложения, который обеспечивает интерфейс настройки и применения фильтров к спискам данных. Включает поддержку табов для различных типов фильтров, интеграцию с react-hook-form для управления состоянием формы, динамическую загрузку форм фильтрации на основе типа элемента, применение и сброс фильтров с сохранением настроек, а также обработку различных типов фильтров через DefaultFilterModalForm.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['react', '@components/ui/layout/modal/components', '@consta/uikit/Modal', '@hooks/use-modals', './components/tabs.component', 'react-hook-form', 'classnames', '@features/filter/types', '@utils/form-object-helper', '@features/filter/helper', './forms/default-filter-modal']
 * @related-files ['src/components/ui/layout/modal/components/header.component.tsx', 'src/features/filter/components/table-modal/forms/default-filter-modal.tsx', 'src/features/filter/helper.ts']
 */

import { useMemo, useRef } from 'react';
import { Header } from '@components/ui/layout/modal/components';
import { Modal } from '@consta/uikit/Modal';
import { useModalQueue } from '@hooks/use-modals';
import { Tabs } from './components/tabs.component';
import { useForm } from 'react-hook-form';
import cn from 'classnames';
import { filterComponent } from '@features/filter/types';
import { getObjectEntity } from '@utils/form-object-helper';
import { getInitialValuesFromFilterControls } from '@features/filter/helper';
import { DefaultFilterModalForm } from './forms/default-filter-modal';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';

export const TableModal = ({
    container,
    isShow,
    setIsShow,
    title = 'Настройки отчёта',
    filterControls,
    applyNewFilterControls,
    fetchDataHadler,
    itemType,
    fetchBtnTitle = 'Сформировать и закрыть',
    className = 'w-[60.0179vmin]'
}: any) => {
    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm<any>({
        defaultValues: getInitialValuesFromFilterControls(filterControls)
    });
    const errorListRef = useRef<ErrorListHandle | null>(null);
    const closeModalHandler = useModalQueue(setIsShow);
    const entity = useMemo<any>(() => getObjectEntity(itemType), [itemType]);
    const modalForm = useMemo<any>(() => entity?.filterModalForm || DefaultFilterModalForm, [entity]);
    const tabs: any[] = useMemo(
        () => [
            { tab: 'filters', label: 'Фильтры', component: modalForm, filters: filterControls },
            {
                tab: 'settings',
                label: 'Дополнительно',
                component: modalForm,
                isExtra: true,
                disabled: !filterControls.filter((uiControl: filterComponent) => uiControl.tab === 'settings').length,
                filters: filterControls
            }
        ],
        [modalForm]
    );

    const submitForm = (data: any) => {
        const newFilterList = filterControls.map((filterControl: any) => {
            if (data[filterControl.name] !== undefined && filterControl.value !== data[filterControl.name]) {
                return { ...filterControl, value: data[filterControl.name] };
            }

            return filterControl;
        });
        applyNewFilterControls(newFilterList);
        fetchDataHadler && fetchDataHadler(data);
    };

    return (
        <Modal
            isOpen={isShow}
            hasOverlay={false}
            onClickOutside={(e: any) => {
                if (!e.target.closest('.dhx_popup')) {
                    closeModalHandler();
                }
            }}
            position="center"
            onEsc={closeModalHandler}
            className={cn('filter-modal flex flex-col', className)}
            container={container}
            style={entity?.filterModalStyle}
        >
            <div className="filter-modal_container m-[3.1756vmin] ">
                <Header
                    title={title}
                    saveBtnTitle={fetchBtnTitle}
                    saveBtnClickHandler={() => {
                        handleSubmit(submitForm)();
                        setTimeout(() => closeModalHandler(), 50);
                    }}
                    closeBtnClickHandler={() => closeModalHandler()}
                />

                <Tabs
                    items={tabs}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    setError={setError}
                    formErrors={errors}
                    watch={watch}
                    clearErrors={clearErrors}
                    errorListRef={errorListRef}
                />
            </div>

            <ErrorList ref={errorListRef} />
        </Modal>
    );
};

// Default export for backward compatibility
export default TableModal;
